import {
  DEFAULT_SERVICE_SLUG,
  canonicalServiceSlug,
  getRouteInfo,
  sectionId,
  serviceRoute,
} from "../config/paths.js";
import { getSeoForRoute } from "../config/seo.js";
import { applySeo } from "../utils/applySeo.js";
import { mountLandingPage } from "../ui/mountLandingPage.js";
import { mountServicesPage } from "../ui/mountServicesPage.js";
import { mountProductPage } from "../ui/mountProductPage.js";
import { mountProductDetailPage } from "../ui/mountProductDetailPage.js";
import { mountSignupPage } from "../ui/mountSignupPage.js";
import { mountLoginPage } from "../ui/mountLoginPage.js";
import { mountInsightDetailPage } from "../ui/mountInsightDetailPage.js";
import { mountInsightsPage } from "../ui/mountInsightsPage.js";
import { mountAboutPage } from "../ui/mountAboutPage.js";
import { mountPrivacyPage } from "../ui/mountPrivacyPage.js";
import { mountTermsPage } from "../ui/mountTermsPage.js";
import { ScrollReveal } from "../ui/ScrollReveal.js";
import { CustomCursor } from "../ui/CustomCursor.js";
import { Experience } from "../three/core/Experience.js";
import { scrollToPageSection } from "../ui/components/Nav.js";

function mountForRoute(root, route) {
  if (route.page === "service") {
    return mountServicesPage(root, route.slug || DEFAULT_SERVICE_SLUG);
  }
  if (route.page === "product-detail") {
    return mountProductDetailPage(root, route.slug);
  }
  if (route.page === "product") {
    return mountProductPage(root);
  }
  if (route.page === "insight-detail") {
    return mountInsightDetailPage(root, route.slug);
  }
  if (route.page === "insights") {
    return mountInsightsPage(root);
  }
  if (route.page === "about") {
    return mountAboutPage(root);
  }
  if (route.page === "privacy") {
    return mountPrivacyPage(root);
  }
  if (route.page === "terms") {
    return mountTermsPage(root);
  }
  if (route.page === "signup") {
    return mountSignupPage(root);
  }
  if (route.page === "login") {
    return mountLoginPage(root);
  }
  return mountLandingPage(root);
}

export class App {
  constructor(root) {
    this.root = root;
    const route = getRouteInfo();

    // /services → /services/web-development
    if (route.page === "service") {
      const path = window.location.pathname.replace(/\/+$/, "");
      const base = (import.meta.env.BASE_URL || "/").replace(/\/+$/, "");
      const servicesRoot = `${base}/services`;
      if (path === servicesRoot || path.toLowerCase() === servicesRoot.toLowerCase()) {
        window.location.replace(serviceRoute(DEFAULT_SERVICE_SLUG));
        return;
      }

      const rawSlug = path.slice(servicesRoot.length).replace(/^\/+|\/+$/g, "").split("/")[0];
      const canonical = canonicalServiceSlug(rawSlug);
      if (rawSlug && canonical !== rawSlug.toLowerCase()) {
        window.location.replace(`${serviceRoute(canonical)}${window.location.hash || ""}`);
        return;
      }
    }

    // Clear prerendered SEO shell before mounting the live UI
    root.replaceChildren();

    const mountResult = mountForRoute(root, route);
    if (!mountResult) return;

    applySeo(route, getSeoForRoute(route));

    this.updateNavActive = mountResult.updateNavActive;
    this.experience = null;

    new ScrollReveal().observe(root);
    new CustomCursor();

    if (mountResult.canvas) {
      try {
        this.experience = new Experience(mountResult.canvas);
        this.experience.registerScrollSections(mountResult.sections);
        this.#bindEvents();
      } catch (error) {
        console.error("Background canvas failed to start", error);
      }
    }

    this.#startLoop();
    this.#scrollToHash();
  }

  #scrollToHash() {
    const id = sectionId(window.location.hash);
    if (!id) return;

    if ("scrollRestoration" in history) {
      history.scrollRestoration = "manual";
    }

    const scroll = () => {
      const target = document.getElementById(id);
      if (!target) return;
      scrollToPageSection(target, { behavior: "auto" });
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(scroll);
    });
    window.setTimeout(scroll, 80);
    window.setTimeout(scroll, 240);
  }

  #bindEvents() {
    window.addEventListener("resize", () => this.experience?.resize());
  }

  #startLoop() {
    const tick = () => {
      requestAnimationFrame(tick);
      this.experience?.update();
      this.updateNavActive?.();
    };
    tick();
  }
}
